import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { ReactElement, ReactNode } from 'react';

import { Badge, BadgeColor } from '../badge/Badge';
import { Icon } from '../icon/Icon';
import { Tooltip, TooltipProps, TooltipTrigger } from '../tooltip/Tooltip';
import styles from './TabLines.module.scss';

/** Shape of a single tab passed to `<TabLines tabs>`. */
export interface Tab<T> {
  /** Stable identifier for the tab. Used as the React key, the active-tab match, and to derive `aria-controls` ids. */
  tabKey: T;
  /** Visible label rendered inside the tab button. */
  tabLabel?: ReactNode;
  /** Numerical badge rendered next to the label (e.g. unread count). */
  badgeLabel?: string;
  /** Color of the numerical badge. */
  badgeColor?: BadgeColor;
  /** Reserved for an image-based tab; unused by the current implementation. */
  imgNode?: ReactNode;
  /** Optional icon rendered before the label inside the tab button. */
  iconName?: SvgIconNames;
  /** Extra element rendered at the bottom of the tab button (e.g. an underline indicator). */
  customBottomElement?: ReactElement;
  /** Optional tooltip props (without `children`) — shown on hover when `label` is set and the tab is not disabled. */
  tooltip?: Omit<TooltipProps, 'children'>;
  /** Content rendered in the panel area when this tab is active. */
  renderPanel?: ReactNode;
  /** When true, the tab button is disabled and click events are ignored. */
  disabled?: boolean;
}

export interface TabLinesProps<T> {
  /** Tabs to render, in display order. */
  tabs: Tab<T>[];
  /** Key of the currently-selected tab (controlled). */
  activeTab: T;
  /** When true, tabs share equal width across the container instead of sizing to content. */
  fullWidth?: boolean;
  /** Extra class forwarded to the section wrapper. */
  className?: string;
  /** Called with `(index, tabKey)` when a tab is clicked. */
  onChange: (index: number, tabKey: T) => void;
}

/**
 * Horizontal tab bar with an associated panel, generic over the tab
 * key type.
 *
 * Each tab can include an icon, a numerical badge, and a tooltip;
 * tabs can be individually disabled. The active tab's `renderPanel` is
 * shown below the bar with appropriate ARIA tab/tabpanel wiring.
 */
export const TabLines = <T extends string>({
  tabs,
  activeTab,
  onChange,
  fullWidth,
  className,
  ...rest
}: TabLinesProps<T>) => {
  const currentPanel = tabs.find(i => i.tabKey === activeTab)?.renderPanel;

  return (
    <section
      className={clsx(styles.tabContainer, className)}
      data-testid="tab-lines"
      {...rest}
    >
      <header className={styles.header} data-tab-header role="tablist">
        {tabs.map(
          (
            {
              tabKey,
              tabLabel,
              badgeLabel,
              badgeColor,
              tooltip,
              iconName,
              customBottomElement,
              disabled = false,
            },
            index
          ) => {
            const isActive = activeTab === tabKey;
            const tabId = `tablines-tab-${tabKey}`;
            const panelId = `tablines-panel-${tabKey}`;

            return (
              <Tooltip
                key={tabKey}
                disabled={disabled || !tooltip?.label}
                label={tooltip?.label}
                closeLabel={tooltip?.closeLabel ? tooltip.closeLabel : ''}
                isTouchDevice={
                  tooltip?.isTouchDevice ? tooltip.isTouchDevice : false
                }
                strategy="fixed"
                placement="top"
                trigger={[TooltipTrigger.Hover]}
                inPortal
              >
                <div
                  className={clsx(styles.tab, {
                    [styles.tabFullWidth]: fullWidth,
                  })}
                  data-testid={`tab-container-${tabKey}`}
                  data-tab
                >
                  <button
                    type="button"
                    id={tabId}
                    className={clsx(styles.tabButton, {
                      [styles.tabButtonFullWidth]: fullWidth,
                      [styles.tabButtonActive]: isActive,
                    })}
                    onClick={() => {
                      onChange(index, tabKey);
                    }}
                    data-testid={`tab-${tabKey}`}
                    disabled={disabled}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={panelId}
                    data-tab-button
                  >
                    {iconName && (
                      <span className={styles.iconWrapper}>
                        <Icon
                          className={clsx(styles.tabIcon, {
                            [styles.tabIconActive]: isActive,
                          })}
                          iconName={iconName}
                          size={20}
                          fillColor="var(--text-on-primary)"
                        />
                      </span>
                    )}
                    {tabLabel}
                    {customBottomElement}
                  </button>
                  {badgeLabel && (
                    <div data-testid="tab-badge">
                      <Badge
                        className={styles.badge}
                        label={badgeLabel}
                        variant="numerical"
                        color={badgeColor}
                      />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          }
        )}
      </header>
      <div
        className={styles.tabPanel}
        role="tabpanel"
        id={`tablines-panel-${activeTab}`}
        aria-labelledby={`tablines-tab-${activeTab}`}
      >
        {currentPanel}
      </div>
    </section>
  );
};
